import { cn } from "@f2e/ui/lib/utils"

import { CopilotHiddenField } from "@/components/Copilot/CopilotHiddenField"

import type {
  AgentTemplateFieldCondition,
  AgentTemplateFormItem,
  AgentTemplateFormValue,
  AgentTemplateFormValues,
  AgentTemplateLocale,
} from "../types"
import { resolveLocalizedText } from "../registry"
import { TemplateItemRenderer } from "./TemplateItemRenderer"

interface TemplateFormRendererProps {
  workspaceId: string
  items: AgentTemplateFormItem[]
  locale: AgentTemplateLocale
  values: AgentTemplateFormValues
  itemMetadata?: Record<string, unknown>
  errors: Record<string, string>
  disabled?: boolean
  renderError: (code: string | undefined) => string | undefined
  onItemPendingChange?: (key: string, pending: boolean) => void
  onItemMetadataChange?: (key: string, metadata: unknown) => void
  onChange: (key: string, value: AgentTemplateFormValue) => void
}

const conditionMatches = (
  condition: AgentTemplateFieldCondition,
  values: AgentTemplateFormValues
) => {
  const rawValue = values[condition.key]
  const value =
    typeof rawValue === "object" &&
    rawValue !== null &&
    "enabled" in rawValue &&
    typeof rawValue.enabled === "boolean"
      ? rawValue.enabled
      : rawValue
  if (condition.is === "non_empty") {
    return typeof value === "string" && value.trim().length > 0
  }
  return value === condition.equals
}

export const TemplateFormRenderer = ({
  workspaceId,
  items,
  locale,
  values,
  itemMetadata,
  errors,
  disabled,
  renderError,
  onItemPendingChange,
  onItemMetadataChange,
  onChange,
}: TemplateFormRendererProps) => {
  const valueItemsByKey = new Map<string, AgentTemplateFormItem>()
  for (const item of items) {
    if ("key" in item) valueItemsByKey.set(item.key, item)
  }
  const visibilityByKey = new Map<string, boolean>()
  const resolvingKeys = new Set<string>()

  const isConditionVisible = (
    condition: AgentTemplateFieldCondition
  ): boolean => {
    const sourceItem = valueItemsByKey.get(condition.key)
    return (
      (!sourceItem || isValueItemVisible(sourceItem)) &&
      conditionMatches(condition, values)
    )
  }

  const isValueItemVisible = (item: AgentTemplateFormItem): boolean => {
    if (!("key" in item)) return true
    const cached = visibilityByKey.get(item.key)
    if (cached !== undefined) return cached
    if (resolvingKeys.has(item.key)) return false
    resolvingKeys.add(item.key)
    const visible = !item.when || isConditionVisible(item.when)
    resolvingKeys.delete(item.key)
    visibilityByKey.set(item.key, visible)
    return visible
  }

  const isVisible = (item: AgentTemplateFormItem) =>
    "key" in item
      ? isValueItemVisible(item)
      : !item.when || isConditionVisible(item.when)

  const visibleValues = Object.fromEntries(
    Object.entries(values).filter(([key]) => {
      const item = valueItemsByKey.get(key)
      return !item || isValueItemVisible(item)
    })
  )
  const visibleAnswers = items.flatMap((item) => {
    if (!("key" in item) || !isVisible(item)) return []
    return [
      {
        key: item.key,
        type: item.type,
        label: resolveLocalizedText(item.label, locale),
        description: item.description
          ? resolveLocalizedText(item.description, locale)
          : undefined,
        required: Boolean(item.required),
        value: visibleValues[item.key],
      },
    ]
  })

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <CopilotHiddenField
        fieldKey="agentTemplate.answers"
        value={JSON.stringify(visibleAnswers)}
      />
      {items.map((item, index) => {
        if (!isVisible(item)) return null
        const hasValue = "key" in item
        const key = hasValue ? item.key : `${item.type}-${index}`
        const width = hasValue ? item.width : "full"
        return (
          <div
            key={key}
            className={cn((width === "full" || !hasValue) && "sm:col-span-2")}
          >
            <TemplateItemRenderer
              workspaceId={workspaceId}
              item={item}
              locale={locale}
              value={hasValue ? values[item.key] : undefined}
              metadata={hasValue ? itemMetadata?.[item.key] : undefined}
              values={visibleValues}
              error={
                hasValue ? renderError(errors[`values.${item.key}`]) : undefined
              }
              disabled={disabled}
              onPendingChange={
                hasValue
                  ? (pending) => onItemPendingChange?.(item.key, pending)
                  : undefined
              }
              onMetadataChange={
                hasValue
                  ? (metadata) => onItemMetadataChange?.(item.key, metadata)
                  : undefined
              }
              onChange={(value) => {
                if (hasValue) onChange(item.key, value)
              }}
            />
          </div>
        )
      })}
    </div>
  )
}
